import { Component, type ErrorInfo, type ReactNode } from "react";
import { looksLikeChunkLoadError, reloadOnceForChunkError } from "@/lib/chunkReload";
import { report } from "@/lib/telemetry";

interface Props {
  children: ReactNode;
  /** Optional label so we can tell which boundary caught it. */
  scope?: string;
}

interface State {
  error: Error | null;
  info: ErrorInfo | null;
  /** A stale/broken lazy chunk was caught; we're reloading to recover. */
  reloading: boolean;
}

/**
 * Catches render/lifecycle errors in the subtree and shows a readable error
 * card instead of letting React unmount the whole app to a blank white screen.
 * The message + component stack are surfaced on-screen (and logged) so a crash
 * is diagnosable in production instead of silently blanking.
 */
class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null, reloading: false };

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Show a neutral "updating" state (not the crash card) while we decide
    // whether this is a recoverable stale-chunk error.
    return { error, reloading: looksLikeChunkLoadError(String(error?.message || "")) };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // A stale/broken lazy chunk after a deploy: reload once to pull fresh
    // assets instead of showing a scary crash card. Guarded against loops — if
    // it already reloaded recently, fall through to the error card below.
    if (looksLikeChunkLoadError(String(error?.message || ""))) {
      if (reloadOnceForChunkError()) return; // reloading; keep the updating state
      this.setState({ reloading: false, info });
      return;
    }
    // Keep the full details in the console for support/debugging.
    console.error(`[ErrorBoundary${this.props.scope ? `:${this.props.scope}` : ""}]`, error, info);
    report(error, { level: "error", tags: { boundary: this.props.scope || "app" }, extra: { componentStack: info.componentStack } });
    this.setState({ info });
  }

  private reset = () => this.setState({ error: null, info: null, reloading: false });

  render() {
    const { error, info, reloading } = this.state;
    if (!error) return this.props.children;

    if (reloading) {
      return (
        <div className="min-h-[60vh] flex items-center justify-center p-6">
          <p className="text-sm text-muted-foreground">Updating to the latest version…</p>
        </div>
      );
    }

    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6">
        <div className="max-w-2xl w-full rounded-lg border border-destructive/40 bg-destructive/5 p-6 space-y-4">
          <div className="space-y-1">
            <h1 className="text-lg font-semibold text-destructive">Something went wrong</h1>
            <p className="text-sm text-muted-foreground">
              This part of the app hit an error and couldn't render. The details below help us fix it.
            </p>
          </div>

          {/* `translate="no"` (+ the `notranslate` class) keeps Google Translate /
              Safari auto-translate from rewriting the error text — a translated
              message ("El objeto no se encuentra aquí.") is undiagnosable, so the
              raw English error and the crashing route must survive verbatim. */}
          <pre
            translate="no"
            className="notranslate text-xs bg-background/60 border border-border rounded p-3 overflow-auto max-h-64 whitespace-pre-wrap"
          >
            {typeof window !== "undefined" ? `route: ${window.location.pathname}${window.location.search}\n\n` : ""}
            {error.message}
            {info?.componentStack ? `\n${info.componentStack}` : ""}
          </pre>

          <div className="flex gap-2">
            <button
              onClick={this.reset}
              className="inline-flex items-center rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90"
            >
              Try again
            </button>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center rounded-md border border-border px-3 py-1.5 text-sm font-medium hover:bg-accent"
            >
              Reload page
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
